import { PanelSection, SkeletonText } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';

import { useDeleteEntityTripMutation } from 'common/hooks/apis/useDeleteEntityTripMutation';
import { useUpdateEntityTripMutation } from 'common/hooks/apis/useUpdateEntityTripMutation';
import { useNotifications } from 'modules/app/notifications';
import { TripSelector } from 'modules/requests/components/TripSelector';
import { PanelSectionListItem } from 'src/core/common/components/Panel';
import { QuerySuspenseBase } from 'src/core/common/components/QuerySuspenseBase/QuerySuspenseBase';
import { UnexpectedErrorCallout } from 'src/core/common/components/UnexpectedErrorCallout/UnexpectedErrorCallout';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';
import {
  useInvalidateEntityTripCache,
  useQueryEntityTrip,
  useUpdateEntityTripCache,
} from 'src/core/common/hooks/apis/useQueryEntityTrip';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { TripBudget } from 'src/core/modules/budgets/components/BudgetOverview/TripBudget/TripBudget';

type Props = {
  entityId: string;
  isEditable?: boolean;
  entityType: 'requests' | 'payments' | 'payables';
  impersonatedUserId?: string;
};

export const TripSection = ({
  entityType,
  entityId,
  isEditable,
  impersonatedUserId,
}: Props) => {
  const { t } = useTranslation('global');
  const { successNotif, dangerNotif } = useNotifications();

  const hasTravelBudgetRequestsFeature = useFeature(
    FEATURES.TRAVEL_BUDGET_REQUEST,
  );

  const entityTripQuery = useQueryEntityTrip({
    entityId,
    entityType,
    isEnabled: hasTravelBudgetRequestsFeature,
  });
  const { mutateAsync: putEntityTrip } = useUpdateEntityTripMutation({
    entityId,
    entityType,
  });
  const { mutateAsync: deleteEntityTrip } = useDeleteEntityTripMutation({
    entityId,
    entityType,
  });

  const updateEntityTripCache = useUpdateEntityTripCache();
  const invalidateEntityTripCache = useInvalidateEntityTripCache();

  const [selectedTripId, setSelectedTripId] = useState<string | undefined>();

  useEffect(() => {
    if (!selectedTripId && entityTripQuery.data?.trip?.id) {
      setSelectedTripId(entityTripQuery.data.trip.id);
    }
  }, [entityTripQuery.data?.trip?.id]);

  if (!hasTravelBudgetRequestsFeature || !entityTripQuery.isSuccess) {
    return null;
  }

  const updateTrip = async () => {
    // no change
    if (
      entityTripQuery.isSuccess &&
      entityTripQuery.data?.trip?.id === selectedTripId
    ) {
      successNotif(t('requests.panel.travelBudget.tripSelect.successUpdate'));
      return;
    }
    try {
      if (selectedTripId === undefined) {
        await deleteEntityTrip();
        updateEntityTripCache(entityType, entityId, undefined);
      } else {
        await putEntityTrip(selectedTripId); // note we can't use the response here as it doesn't have budget enrichment
        invalidateEntityTripCache(entityType, entityId);
      }
      successNotif(t('requests.panel.travelBudget.tripSelect.successUpdate'));
    } catch {
      dangerNotif(t('requests.panel.travelBudget.tripSelect.errorUpdate'));
    }
  };

  const renderTripEdition = () => {
    return (
      <TripSelector
        className="mb-24"
        setSelectedTripId={(id) => {
          setSelectedTripId(id);
        }}
        selectedTripId={selectedTripId}
        showBudgetBreakdown
        impersonatedUserId={impersonatedUserId}
      />
    );
  };

  return (
    <ErrorBoundary
      context={{
        team: 'travel-and-expenses',
        scope: 'TripSection',
      }}
    >
      <PanelSection
        title={t('requests.panel.travelBudget.tripSelect.title')}
        {...(isEditable
          ? {
              isEditable: true,
              editSection: renderTripEdition(),
              cancelTranslation: t('misc.cancel'),
              saveTranslation: t('misc.saveChanges'),
              onSave: updateTrip,
              onCancel: () => {
                setSelectedTripId(undefined);
              },
            }
          : {
              isEditable: false,
            })}
      >
        <QuerySuspenseBase
          queryState={entityTripQuery}
          loading={<SkeletonText size="xl" width="100%" />}
          fallback={() => <UnexpectedErrorCallout />}
        >
          {({ trip }) => {
            if (!trip) {
              return (
                <PanelSectionListItem
                  label={t('requests.panel.travelBudget.newLinkLabel')}
                  value={t('misc.no')}
                />
              );
            }
            return <TripBudget trip={trip} showHeader />;
          }}
        </QuerySuspenseBase>
      </PanelSection>
    </ErrorBoundary>
  );
};
